/**
 * AppSettingsScreen.jsx
 * App-Einstellungen mit Zahlungsmethoden
 */
import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, CreditCard, Plus, Trash2, Check,
  Bell, Moon, Sun, Globe, Shield, Info, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const AppSettingsScreen = ({ portalToken, onBack, onNavigate }) => {
  const { t, i18n } = useTranslation();
  const [activeSection, setActiveSection] = useState('main'); // main, payment, notifications
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [addingPayment, setAddingPayment] = useState(false);
  const [settings, setSettings] = useState({
    pushNotifications: true,
    emailNotifications: true,
    darkMode: true,
    language: 'de'
  });

  // Fetch payment methods
  useEffect(() => {
    if (activeSection === 'payment' && portalToken) {
      fetchPaymentMethods();
    }
  }, [activeSection, portalToken]);

  const fetchPaymentMethods = async () => {
    setLoadingPayments(true);
    try {
      const response = await axios.get(`${API}/portal/payment-methods`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setPaymentMethods(response.data.payment_methods || []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    setAddingPayment(true);
    try {
      const response = await axios.post(`${API}/portal/payment-methods/add`, {}, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      
      if (response.data.payment_link) {
        window.open(response.data.payment_link, '_blank');
        toast.success(t('common.loading'));
        // Refresh after a delay
        setTimeout(() => fetchPaymentMethods(), 3000);
      }
    } catch (error) {
      console.error('Error adding payment method:', error);
      toast.error(t('common.error'));
    } finally {
      setAddingPayment(false);
    }
  };

  const handleDeletePaymentMethod = async (methodId) => {
    try {
      await axios.delete(`${API}/portal/payment-methods/${methodId}`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      toast.success(t('common.success'));
      fetchPaymentMethods();
    } catch (error) {
      console.error('Error deleting payment method:', error);
      toast.error(t('common.error'));
    }
  };

  // Main settings menu
  if (activeSection === 'main') {
    return (
      <div className="h-full overflow-y-auto p-4 space-y-3 pb-20">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button onClick={onBack} className="p-2 rounded-full bg-zinc-800 hover:bg-zinc-700">
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-white text-lg font-bold">{t('settings.title')}</h1>
        </div>

        {/* Payment Methods Section */}
        <button 
          onClick={() => setActiveSection('payment')}
          className="w-full bg-zinc-800/50 rounded-xl p-4 flex items-center gap-4 hover:bg-zinc-800/70"
        >
          <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-green-400" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-white font-medium">{t('settings.payment_methods', 'Zahlungsmethoden')}</p>
            <p className="text-zinc-400 text-xs">{t('settings.payment_desc', 'Kreditkarten & Lastschrift verwalten')}</p>
          </div>
          <ChevronLeft className="w-5 h-5 text-zinc-500 rotate-180" />
        </button>

        {/* Notifications */}
        <div className="bg-zinc-800/50 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-zinc-700/50">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-yellow-400" />
              <p className="text-white font-medium">{t('settings.notifications_label', 'Benachrichtigungen')}</p>
            </div>
          </div>
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-zinc-300 text-sm">{t('settings.push_notifications', 'Push-Benachrichtigungen')}</span>
              <button 
                onClick={() => setSettings(s => ({...s, pushNotifications: !s.pushNotifications}))}
                className={`w-12 h-6 rounded-full transition-colors ${settings.pushNotifications ? 'bg-green-500' : 'bg-zinc-600'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${settings.pushNotifications ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-300 text-sm">{t('settings.email_notifications', 'E-Mail-Benachrichtigungen')}</span>
              <button 
                onClick={() => setSettings(s => ({...s, emailNotifications: !s.emailNotifications}))}
                className={`w-12 h-6 rounded-full transition-colors ${settings.emailNotifications ? 'bg-green-500' : 'bg-zinc-600'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${settings.emailNotifications ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Theme */}
        <div className="bg-zinc-800/50 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            {settings.darkMode ? <Moon className="w-5 h-5 text-purple-400" /> : <Sun className="w-5 h-5 text-yellow-400" />}
            <p className="text-white font-medium">{t('settings.appearance', 'Erscheinungsbild')}</p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => setSettings(s => ({...s, darkMode: false}))}
              className={`flex-1 p-3 rounded-lg border ${!settings.darkMode ? 'border-yellow-500 bg-yellow-500/10' : 'border-zinc-700 bg-zinc-800'}`}
            >
              <Sun className="w-5 h-5 mx-auto mb-1 text-yellow-400" />
              <p className="text-xs text-center text-zinc-300">{t('settings.light', 'Hell')}</p>
            </button>
            <button 
              onClick={() => setSettings(s => ({...s, darkMode: true}))}
              className={`flex-1 p-3 rounded-lg border ${settings.darkMode ? 'border-purple-500 bg-purple-500/10' : 'border-zinc-700 bg-zinc-800'}`}
            >
              <Moon className="w-5 h-5 mx-auto mb-1 text-purple-400" />
              <p className="text-xs text-center text-zinc-300">{t('settings.dark', 'Dunkel')}</p>
            </button>
          </div>
        </div>

        {/* Language */}
        <div className="bg-zinc-800/50 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <Globe className="w-5 h-5 text-blue-400" />
            <p className="text-white font-medium">{t('settings.language')}</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { code: 'de', flag: '🇩🇪', label: 'Deutsch' },
              { code: 'en', flag: '🇬🇧', label: 'English' },
              { code: 'fr', flag: '🇫🇷', label: 'Français' },
              { code: 'it', flag: '🇮🇹', label: 'Italiano' },
              { code: 'es', flag: '🇪🇸', label: 'Español' },
            ].map(lang => (
              <button 
                key={lang.code}
                onClick={() => {
                  i18n.changeLanguage(lang.code);
                  setSettings(s => ({...s, language: lang.code}));
                  localStorage.setItem('mobile_app_language', lang.code);
                }}
                className={`p-2.5 rounded-lg border transition-colors ${
                  i18n.language?.startsWith(lang.code) 
                    ? 'border-blue-500 bg-blue-500/10' 
                    : 'border-zinc-700 bg-zinc-800'
                }`}
                data-testid={`lang-${lang.code}`}
              >
                <span className="text-lg block text-center mb-0.5">{lang.flag}</span>
                <p className="text-[10px] text-center text-zinc-300">{lang.label}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Privacy & About */}
        <div className="bg-zinc-800/50 rounded-xl overflow-hidden">
          <button className="w-full p-4 flex items-center gap-4 border-b border-zinc-700/50 hover:bg-zinc-800/70">
            <Shield className="w-5 h-5 text-zinc-400" />
            <span className="text-zinc-300 text-sm flex-1 text-left">Datenschutz</span>
            <ChevronLeft className="w-4 h-4 text-zinc-500 rotate-180" />
          </button>
          <button className="w-full p-4 flex items-center gap-4 hover:bg-zinc-800/70">
            <Info className="w-5 h-5 text-zinc-400" />
            <span className="text-zinc-300 text-sm flex-1 text-left">Über die App</span>
            <span className="text-zinc-500 text-xs">v2.0.1</span>
          </button>
        </div>
      </div>
    );
  }

  // Payment methods section
  if (activeSection === 'payment') {
    return (
      <div className="h-full overflow-y-auto p-4 space-y-3 pb-20">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => setActiveSection('main')} className="p-2 rounded-full bg-zinc-800 hover:bg-zinc-700">
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-white text-lg font-bold">Zahlungsmethoden</h1>
        </div>

        {/* Add new payment method */}
        <button 
          onClick={handleAddPaymentMethod}
          disabled={addingPayment}
          className="w-full bg-gradient-to-r from-green-500/20 to-emerald-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-4 hover:from-green-500/30"
        >
          <div className="w-10 h-10 bg-green-500/30 rounded-lg flex items-center justify-center">
            {addingPayment ? (
              <RefreshCw className="w-5 h-5 text-green-400 animate-spin" />
            ) : (
              <Plus className="w-5 h-5 text-green-400" />
            )}
          </div>
          <div className="flex-1 text-left">
            <p className="text-white font-medium">Neue Zahlungsmethode</p>
            <p className="text-green-400 text-xs">Kreditkarte oder SEPA hinzufügen</p>
          </div>
        </button>

        {/* Payment methods list */}
        {loadingPayments ? (
          <div className="bg-zinc-800/50 rounded-xl p-8 text-center">
            <RefreshCw className="w-8 h-8 text-yellow-400 animate-spin mx-auto mb-3" />
            <p className="text-zinc-400">Lade Zahlungsmethoden...</p>
          </div>
        ) : paymentMethods.length === 0 ? (
          <div className="bg-zinc-800/50 rounded-xl p-8 text-center">
            <CreditCard className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">Keine Zahlungsmethoden hinterlegt</p>
            <p className="text-zinc-500 text-sm mt-1">Fügen Sie eine Kreditkarte oder Bankverbindung hinzu</p>
          </div>
        ) : (
          <div className="space-y-2">
            {paymentMethods.map((method, index) => (
              <div key={method.id || index} className="bg-zinc-800/50 rounded-xl p-4 flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  method.type === 'card' ? 'bg-blue-500/20' : 'bg-purple-500/20'
                }`}>
                  <CreditCard className={`w-5 h-5 ${method.type === 'card' ? 'text-blue-400' : 'text-purple-400'}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-medium">
                      {method.brand || method.type?.toUpperCase() || 'Karte'} •••• {method.last4 || '****'}
                    </p>
                    {method.is_default && (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-[10px] rounded-full flex items-center gap-1">
                        <Check className="w-3 h-3" /> Standard
                      </span>
                    )}
                  </div>
                  <p className="text-zinc-500 text-xs">
                    {method.exp_month && method.exp_year ? `Gültig bis ${method.exp_month}/${method.exp_year}` : method.bank_name || ''}
                  </p>
                </div>
                <button 
                  onClick={() => handleDeletePaymentMethod(method.id)}
                  className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20"
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Security info */}
        <div className="bg-zinc-800/30 rounded-xl p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-zinc-400 mt-0.5" />
          <div>
            <p className="text-zinc-400 text-xs">
              Ihre Zahlungsdaten werden sicher bei unserem Zahlungsanbieter gespeichert.
              Wir haben keinen Zugriff auf Ihre vollständigen Kartendaten.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AppSettingsScreen;
